import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { toast } from "react-toastify";

// Components and Icons
import Header from "../../components/NewHeader/NewHeader";
import Footer from "../../components/Footer";
import MiniLoader from "../../components/MiniLoader/MiniLoader";
import AppLoader from "../../components/AppLoader";
import { ReactComponent as Delivered } from "../../assets/icons/checkmark.svg";
import { ReactComponent as Logistics } from "../../assets/icons/truck.svg";
import { ReactComponent as Designer } from "../../assets/icons/palette.svg";
import { ReactComponent as Production } from "../../assets/icons/production.svg";

// Styled Components
import {
  Container,
  MainContainer,
  HeaderWrapper,
  TableContainer,
  TableRow,
  TableHeaderRow,
  Label,
  Number,
  Column,
  OrderContainer,
  OrderTitle,
  OrderLightText,
  AddContainer,
  Plus,
  Button,
  LoaderContainer,
  CreateText,
} from "./OpsPage.styles";

// Redux Actions and Selectors
import {
  selectRedirectTo,
  setRedirectTo,
} from "../../store/slices/common/slice";
import { logout } from "../../store/slices/businessUser/asyncThunks";
import {
  getOrdersForDesigner,
  updateOrderStateDesigner,
} from "../../store/slices/marketplace/asyncThunks";
import { selectBusinessUserInfo } from "../../store/slices/businessUser/slice";

// Utilities
import { formatDate } from "../../utils/utils";
import { businessUserRoles } from "../../utils/constants";

const initialData = [
  {
    id: 1,
    title: "mockUp",
    total: 0,
    data: [],
    icon: <Designer />,
  },
  {
    id: 2,
    title: "production",
    total: 0,
    data: [],
    icon: <Production />,
  },
  {
    id: 3,
    title: "logistics",
    total: 0,
    data: [],
    icon: <Logistics />,
  },
  {
    id: 4,
    title: "delivered",
    total: 0,
    data: [],
    icon: <Delivered />,
  },
];

const OpsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollUpY, setLastScrollUpY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(null);
  const lastScrollTopRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [data, setData] = useState(initialData);
  const businessUser = useSelector(selectBusinessUserInfo);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigation = useNavigate();
  const redirectTo = useSelector(selectRedirectTo);

  useEffect(() => {
    if (redirectTo) {
      navigation(redirectTo);
      dispatch(setRedirectTo({ redirectTo: "" }));
    }
  }, [dispatch, navigation, redirectTo]);

  useEffect(() => {
    const redirect = searchParams.get("redirect");
    if (dispatch && redirect) {
      dispatch(setRedirectTo({ redirectTo: redirect }));
      setSearchParams({});
    }
  }, [dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(getOrdersForDesigner())
      .unwrap()
      .then((response) => {
        setLoading(false);
        setData([
          {
            id: 1,
            title: "mockUp",
            total: response?.data?.mockUps?.length,
            data: response?.data?.mockUps ? response?.data?.mockUps : [],
            icon: <Designer />,
          },
          {
            id: 2,
            title: "production",
            total: response?.data?.production?.length,
            data: response?.data?.production ? response?.data?.production : [],
            icon: <Production />,
          },
          {
            id: 3,
            title: "logistics",
            total: response?.data?.logistics?.length,
            data: response?.data?.logistics ? response?.data?.logistics : [],
            icon: <Logistics />,
          },
          {
            id: 4,
            title: "delivered",
            total: response?.data?.delivered?.length,
            data: response?.data?.delivered ? response?.data?.delivered : [],
            icon: <Delivered />,
          },
        ]);
      })
      .catch((err) => toast.error(err.message));
  }, [dispatch]);

  const handleOnDragEnd = (result) => {
    const { source, destination } = result;

    // Dropped outside a droppable area
    if (!destination) {
      return;
    }

    // If dropped in the same spot
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      return; // Do nothing if dropped in the same TableRow
    }

    const sourceTable = data.find(
      (table) => table.id === parseInt(source.droppableId)
    );
    const destinationTable = data.find(
      (table) => table.id === parseInt(destination.droppableId)
    );

    const sourceItems = Array.from(sourceTable.data);
    const [movedItem] = sourceItems.splice(source.index, 1);
    const destinationItems = Array.from(destinationTable.data);
    destinationItems.splice(destination.index, 0, movedItem);

    const newData = data.map((table) => {
      if (table.id === sourceTable.id) {
        return { ...table, data: sourceItems };
      }
      if (table.id === destinationTable.id) {
        return { ...table, data: destinationItems };
      }
      return table;
    });

    setData(newData);
    // Updating the state of the moved order
    const newState = destinationTable.title;
    setUpdateLoading(true);
    dispatch(
      updateOrderStateDesigner({ id: movedItem.id, body: { state: newState } })
    )
      .unwrap()
      .then(() => {
        dispatch(getOrdersForDesigner())
          .unwrap()
          .then((response) => {
            setUpdateLoading(false);

            setData([
              {
                id: 1,
                title: "mockUp",
                total: response?.data?.mockUps?.length,
                data: response?.data?.mockUps ? response?.data?.mockUps : [],
                icon: <Designer />,
              },
              {
                id: 2,
                title: "production",
                total: response?.data?.production?.length,
                data: response?.data?.production
                  ? response?.data?.production
                  : [],
                icon: <Production />,
              },
              {
                id: 3,
                title: "logistics",
                total: response?.data?.logistics?.length,
                data: response?.data?.logistics
                  ? response?.data?.logistics
                  : [],
                icon: <Logistics />,
              },
              {
                id: 4,
                title: "delivered",
                total: response?.data?.delivered?.length,
                data: response?.data?.delivered
                  ? response?.data?.delivered
                  : [],
                icon: <Delivered />,
              },
            ]);
          })
          .catch((err) => toast.error(err.message));
      })
      .catch((err) => {
        toast.error("Failed to update order state");
        setData(data);
      });
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollTop = window.scrollY;
      const lastScrollTop = lastScrollTopRef.current;

      if (currentScrollTop === 0) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
        setScrollDirection(null);
        lastScrollTopRef.current = currentScrollTop;
        return;
      }

      if (currentScrollTop > lastScrollTop) {
        setScrollDirection("down");
        setLastScrollUpY(currentScrollTop);
      } else {
        setScrollDirection("up");
      }

      if (scrollDirection === "up" && currentScrollTop < lastScrollUpY - 350) {
        setHeaderVisible(true);
        setLastScrollUpY(currentScrollTop);
      }

      if (
        scrollDirection === "down" &&
        currentScrollTop > lastScrollUpY + 350
      ) {
        setHeaderVisible(false);
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    const handleScrollDebounced = () => {
      requestAnimationFrame(handleScroll);
    };

    window.addEventListener("scroll", handleScrollDebounced);

    return () => {
      window.removeEventListener("scroll", handleScrollDebounced);
    };
  }, [scrollDirection]);

  // Determine whether dragging is disabled based on user role
  const getIsDragDisabled = (droppableId) => {
    // if (businessUser.role === "designer" && droppableId === "1") return false;
    // if (businessUser.role === "production" && droppableId === "2") return false;
    // if (businessUser.role === "logistics" && droppableId === "3") return false;
    if (businessUser.role === "superAdmin") {
      return false;
    } else return true;
  };
  const [hover, setHover] = useState(false);
  return (
    <MainContainer>
      <HeaderWrapper
        style={{
          opacity: headerVisible ? 1 : 0,
          marginTop: headerVisible ? 0 : -150,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Header />
      </HeaderWrapper>

      {updateLoading ? (
        <AppLoader size={24} />
      ) : (
        <AppLoader size={24} opacity={0} />
      )}
      <Container>
        {!loading ? (
          <DragDropContext onDragEnd={handleOnDragEnd}>
            {data.map((v) => (
              <TableContainer key={v.id}>
                <TableHeaderRow>
                  <Column>
                    <Number>{v.total}</Number>
                    <Label>{t(v.title)}</Label>
                  </Column>
                  {v.icon}
                </TableHeaderRow>
                <Droppable droppableId={v.id.toString()} key={v.id}>
                  {(provided) => (
                    <div
                      style={{
                        overflow: "hidden",
                        borderRadius: 12,
                        boxShadow: "1px 1px 4px 0px rgba(72, 72, 136, 0.35)",
                      }}
                    >
                      <TableRow
                        data-table-id={v.id}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {v.data.map((order, index) => (
                          <Draggable
                            key={order.id}
                            draggableId={order.id.toString()}
                            index={index}
                            isDragDisabled={
                              getIsDragDisabled(v.id.toString()) ||
                              updateLoading
                            }
                          >
                            {(provided) => (
                              <OrderContainer
                                onClick={() => {
                                  if (!updateLoading) {
                                    navigate(
                                      `/ops-portal-order-details/${order.id}`
                                    );
                                  }
                                }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Column>
                                  <OrderTitle>
                                    {t("order")}# {order.id}
                                  </OrderTitle>
                                  <OrderLightText>
                                    {order?.marketplaceOrderItems?.length}
                                  </OrderLightText>
                                </Column>
                                <OrderLightText>
                                  {formatDate(order.createdAt)}
                                </OrderLightText>
                              </OrderContainer>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableRow>
                    </div>
                  )}
                </Droppable>
                {businessUserRoles.superAdmin === businessUser.role &&
                  v.id === 1 && (
                    <AddContainer
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                      onClick={() => navigate("/ops-portal-create-order")}
                    >
                      <Plus>+</Plus>
                      <CreateText $hovered={hover}>
                        {t("create-order")}
                      </CreateText>
                    </AddContainer>
                  )}
              </TableContainer>
            ))}
          </DragDropContext>
        ) : (
          <LoaderContainer>
            <MiniLoader />
          </LoaderContainer>
        )}
      </Container>
      <Button
        $red
        onClick={() => {
          dispatch(logout())
            .unwrap()
            .then(() => {
              navigate("/");
            });
        }}
      >
        Logout
      </Button>
      <Footer />
    </MainContainer>
  );
};

export default OpsPage;
